import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EntityWorkspace from '../components/EntityWorkspace';
import EntityPivotLink from '../components/EntityPivotLink';
import DeleteButton from '../components/DeleteButton';
import api from '../services/api';
import useEvidenceTimezone from '../hooks/useEvidenceTimezone';

function riskClass(level) {
  return `risk-pill risk-${level || 'medium'}`;
}

function Actors() {
  const navigate = useNavigate();
  const { format, suffix } = useEvidenceTimezone();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    display_name: '',
    actor_type: 'threat_actor',
    risk_level: 'medium',
    summary: '',
  });
  const [creating, setCreating] = useState(false);

  const columns = useMemo(() => [
    {
      key: 'name',
      label: 'Name',
      render: (row) => (
        <EntityPivotLink type="actor" id={row.actor_id} label={row.display_name} showBadge={false} />
      ),
    },
    {
      key: 'type',
      label: 'Type',
      render: (row) => <span className="entity-type-chip">{row.actor_type}</span>,
    },
    {
      key: 'risk',
      label: 'Risk',
      render: (row) => <span className={riskClass(row.risk_level)}>{row.risk_level}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => row.status,
    },
    {
      key: 'case',
      label: 'Case',
      render: (row) => (
        row.case_id ? (
          <EntityPivotLink type="case" id={row.case_id} label={row.case_title || row.case_id} showBadge={false} />
        ) : '—'
      ),
    },
    {
      key: 'updated',
      label: `Updated (${suffix})`,
      render: (row) => <span className="timestamp-cell">{format(row.updated_at)}</span>,
    },
  ], [format, suffix]);

  const filterFields = useMemo(() => [
    {
      key: 'actor_type',
      label: 'Type',
      options: (meta) => meta?.actor_types || [],
    },
    {
      key: 'risk_level',
      label: 'Risk',
      options: (meta) => meta?.risk_levels || [],
    },
    {
      key: 'status',
      label: 'Status',
      options: (meta) => meta?.actor_statuses || [],
    },
  ], []);

  const submitCreate = async (event) => {
    event.preventDefault();
    if (!form.display_name.trim()) return;
    setCreating(true);
    try {
      await api.post('/hdp/actors', form);
      setCreateOpen(false);
      setForm({ display_name: '', actor_type: 'threat_actor', risk_level: 'medium', summary: '' });
      window.location.reload();
    } catch (err) {
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <EntityWorkspace
        title="Threat Actors"
        subtitle="Track intrusion sets, campaigns, and attributed operators across cases."
        breadcrumbs={[
          { label: 'Dashboard', to: '/' },
          { label: 'Threat Actors' },
        ]}
        entityType="actor"
        apiPath="/hdp/actors"
        responseKey="actors"
        rowIdField="actor_id"
        filterFields={filterFields}
        columns={columns}
        headerActions={(
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>
            Create actor
          </button>
        )}
        onRowClick={(row) => navigate(`/actors/${row.actor_id}`)}
        rowActions={(row, reload) => (
          <DeleteButton
            confirmMessage={`Delete actor "${row.display_name}"?`}
            onDelete={async () => {
              await api.delete(`/hdp/actors/${row.actor_id}`);
              reload();
            }}
          />
        )}
        emptyMessage="No threat actors match your filters."
      />

      {createOpen && (
        <div className="modal-backdrop" onClick={() => setCreateOpen(false)}>
          <div className="modal-card entity-create-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create threat actor</h3>
            <form onSubmit={submitCreate}>
              <label className="form-label">Display name</label>
              <input
                className="form-input"
                value={form.display_name}
                onChange={(e) => setForm((prev) => ({ ...prev, display_name: e.target.value }))}
                required
              />
              <label className="form-label">Type</label>
              <select
                className="form-input"
                value={form.actor_type}
                onChange={(e) => setForm((prev) => ({ ...prev, actor_type: e.target.value }))}
              >
                <option value="threat_actor">Threat actor</option>
                <option value="intrusion_set">Intrusion set</option>
                <option value="campaign">Campaign</option>
                <option value="malware_author">Malware author</option>
                <option value="unknown">Unknown</option>
              </select>
              <label className="form-label">Risk level</label>
              <select
                className="form-input"
                value={form.risk_level}
                onChange={(e) => setForm((prev) => ({ ...prev, risk_level: e.target.value }))}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
              <label className="form-label">Summary</label>
              <textarea
                className="form-input"
                rows={3}
                value={form.summary}
                onChange={(e) => setForm((prev) => ({ ...prev, summary: e.target.value }))}
              />
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setCreateOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Actors;
